'use client'
import { useState, useEffect, useMemo } from 'react'
import Link from 'next/link'
import { AIRPORT_DB } from '@/data/airports'

const MIN_DEALS = 3
const MIN_DISCOUNT = 15

function passes(s) { return s && s.count >= MIN_DEALS && s.maxDiscount >= MIN_DISCOUNT }

function Stat({ value, label, sub }) {
  return (
    <div className="bg-white rounded-xl px-4 py-3 text-center border border-blue-100">
      <p className="text-2xl font-bold text-blue-700">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  )
}

export default function AirportDealsPreview({ suggestedOrigin, originStats = {}, yearlyDealsByOrigin = {}, historicalStats = {}, tier = 'guest', className = 'mb-6' }) {
  const computeDefault = () => {
    const origins = Object.keys(originStats)
      .sort((a, b) => (AIRPORT_DB[a]?.city || a).localeCompare(AIRPORT_DB[b]?.city || b))
    if (!origins.length) return null
    if (suggestedOrigin && passes(originStats[suggestedOrigin])) return suggestedOrigin
    const qualifying = Object.entries(originStats).filter(([, s]) => passes(s))
    if (!qualifying.length) return origins[0] ?? null
    return qualifying.sort((a, b) => b[1].count - a[1].count)[0][0]
  }

  const [selected, setSelected] = useState(computeDefault)

  const origins = useMemo(() =>
    Object.keys(originStats)
      .sort((a, b) => (AIRPORT_DB[a]?.city || a).localeCompare(AIRPORT_DB[b]?.city || b))
  , [originStats])

  const bestDefault = useMemo(() => {
    if (!origins.length) return null
    if (suggestedOrigin && passes(originStats[suggestedOrigin])) return suggestedOrigin
    const qualifying = Object.entries(originStats).filter(([, s]) => passes(s))
    if (!qualifying.length) return origins[0] ?? null
    return qualifying.sort((a, b) => b[1].count - a[1].count)[0][0]
  }, [suggestedOrigin, originStats, origins])

  useEffect(() => {
    if (!selected && bestDefault) setSelected(bestDefault)
  }, [bestDefault])

  const stats = selected ? originStats[selected] : null
  const hist  = selected ? historicalStats[selected] : null
  const liveCount = stats && passes(stats) ? stats.count : 0

  if (!bestDefault || (!stats && !hist)) return null

  const isGeo = selected === suggestedOrigin && !!suggestedOrigin
  const airport = AIRPORT_DB[selected]

  const hasHist = hist && (hist.totalDests > 0 || hist.avgDiscount)

  return (
    <div className={`rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 px-5 py-5 ${className}`}>
      <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-1">See what FairFares can do for you</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm text-gray-600 shrink-0">From</span>
        <select
          value={selected || ''}
          onChange={e => setSelected(e.target.value)}
          aria-label="Departure airport"
          className="text-sm font-medium border border-blue-200 rounded-lg px-3 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
        >
          {origins.map(code => (
            <option key={code} value={code}>
              {AIRPORT_DB[code]?.flag || ''} {AIRPORT_DB[code]?.city || code} ({code})
            </option>
          ))}
        </select>
        {isGeo && <span className="text-xs text-gray-500">based on your location</span>}
      </div>

      {liveCount > 0
        ? <p className="text-sm text-gray-500 mb-3"><span className="font-semibold text-blue-700">{liveCount} deals</span> live right now · last 12 months:</p>
        : <p className="text-sm text-gray-500 mb-3">No deals right now · last 12 months:</p>
      }

      {hasHist ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <Stat value={hist.totalDests ?? '—'} label="destinations found" />
          <Stat
            value={hist.europeDests ?? '—'}
            label="European"
            sub={hist.avgEuropePrice ? `avg. €${hist.avgEuropePrice}` : undefined}
          />
          <Stat
            value={hist.longHaulDests ?? '—'}
            label="long-haul"
            sub={hist.avgLongHaulPrice ? `avg. €${hist.avgLongHaulPrice}` : undefined}
          />
          <Stat
            value={hist.avgDiscount ? `−${hist.avgDiscount}%` : '—'}
            label="avg. gap"
            sub="↩ Roundtrip"
          />
          {hist.avgEuropePrice && <Stat value={`€${hist.avgEuropePrice}`} label="avg. European deal" sub="↩ Roundtrip" />}
          {hist.avgLongHaulPrice && <Stat value={`€${hist.avgLongHaulPrice}`} label="avg. long-haul deal" sub="↩ Roundtrip" />}
        </div>
      ) : (
        <div className="bg-white rounded-xl px-4 py-4 mb-4 border border-blue-100 text-center">
          <p className="text-sm text-gray-500">No data yet for this airport</p>
        </div>
      )}

      {tier === 'guest' && (
        <Link href="/signup"
          className="block w-full text-center bg-blue-600 text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
          {liveCount > 0
            ? `Sign up free — unlock all ${liveCount} deals →`
            : 'Sign up free — get notified when deals appear →'}
        </Link>
      )}
      {tier === 'free' && (
        <Link href="/pricing"
          className="block w-full text-center bg-blue-600 text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
          Upgrade to Plus — unlock all airports →
        </Link>
      )}
    </div>
  )
}
